const LEGACY_SERVICE_WORKER_CLEANUP_KEY =
  'blankmile_legacy_service_worker_cleanup_v1';
const CLEANUP_TIMEOUT_MS = 2_000;
const LEGACY_PATHS = ['/', '/join/'];

export async function cleanupLegacyRootServiceWorkerState(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const storage = getCleanupStorage();
  try {
    if (storage?.getItem(LEGACY_SERVICE_WORKER_CLEANUP_KEY)) {
      return;
    }
  } catch {
    // Storage can be unavailable even when accessing localStorage succeeds.
  }

  const deadline = Date.now() + CLEANUP_TIMEOUT_MS;
  async function beforeDeadline<T>(operation: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        operation,
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error('Legacy service worker cleanup timed out')),
            Math.max(0, deadline - Date.now()),
          );
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  try {
    const legacyScopes = new Set(
      LEGACY_PATHS.map((path) => new URL(path, window.location.origin).href),
    );
    const registrations = await beforeDeadline(
      navigator.serviceWorker.getRegistrations(),
    );
    const legacyRegistrations = registrations.filter((registration) =>
      legacyScopes.has(registration.scope),
    );

    const unregistered = await beforeDeadline(
      Promise.all(
        legacyRegistrations.map((registration) => registration.unregister()),
      ),
    );
    if (unregistered.some((success) => !success)) {
      return;
    }

    if ('caches' in window) {
      const cacheKeys = await beforeDeadline(window.caches.keys());
      const legacyCacheKeys = cacheKeys.filter((cacheKey) =>
        LEGACY_PATHS.some((path) => cacheKey.startsWith(`ngsw:${path}:`)),
      );
      await beforeDeadline(
        Promise.all(
          legacyCacheKeys.map((cacheKey) => window.caches.delete(cacheKey)),
        ),
      );
    }

    storage?.setItem(LEGACY_SERVICE_WORKER_CLEANUP_KEY, 'true');
  } catch {
    // Cleanup is best-effort and bounded; app bootstrap must continue.
  }
}

function getCleanupStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
